import { ChartConfig, ChartContainer, ChartTooltip } from "@/components/chart";
import ChartTemplate from "@/components/chart-template";
import { CampaignFilterRow } from "@/http/api-types";
import { cn } from "@/lib/utils";
import { Area, CartesianGrid, ComposedChart, XAxis, YAxis } from "recharts";
import { REASONS_IN_ORDER } from "./reasons";

type StackedAreaChartProps = {
  rows: CampaignFilterRow[] | undefined;
  className?: string;
};

const reasonKey = (r: string) =>
  r
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80); // cap length

const chartConfig: ChartConfig = REASONS_IN_ORDER.reduce((acc, reason, idx) => {
  const colorIndex = (idx % 25) + 1;
  acc[reasonKey(reason)] = {
    label: reason,
    color: `hsl(var(--chart-${colorIndex}))`,
  };
  return acc;
}, {} as ChartConfig);

type Transformed = { timestamp: string } & { [key: string]: number | string };

function transform(rows: CampaignFilterRow[] | undefined): Transformed[] {
  if (!rows?.length) return [];

  const map = new Map<string, Record<string, number>>();
  for (const r of rows) {
    if (!REASONS_IN_ORDER.includes(r.reason)) continue;
    const key = r.timestamp;
    if (!map.has(key)) map.set(key, {});
    const bucket = map.get(key)!;
    bucket[reasonKey(r.reason)] = (bucket[reasonKey(r.reason)] || 0) + r.count;
  }

  return Array.from(map.entries())
    .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
    .map(([ts, counts]) => ({ timestamp: ts, ...counts }));
}

const timeLabelFormatter = (iso: string) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return iso;
  }
};

const dailyAxisFormatter = (iso: string) => {
  try {
    const d = new Date(iso);

    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    console.log("Error formatting date:", iso);
    return "";
  }
};

function StackedAreaChart({ rows, className }: StackedAreaChartProps) {
  const data = transform(rows);
  if (!rows) return null;
  if (!data.length) return <div className={cn("text-sm text-muted-foreground", className)}>No data</div>;

  // We want first reason to appear on top, so we add areas in reverse order
  const reversed = [...REASONS_IN_ORDER].reverse();

  const xTicks = (() => {
    const seen = new Set<string>();
    return data
      .map(r => {
        const dt = new Date(r.timestamp as string);
        const key = `${dt.getUTCFullYear()}-${dt.getUTCMonth()}-${dt.getUTCDate()}`;
        if (!seen.has(key)) {
          seen.add(key);
          return r.timestamp as string;
        }
        return null;
      })
      .filter(Boolean) as string[];
  })();

  return (
    <ChartTemplate
      table={null}
      timeSplittable={false}
      title="Campaign Filter Reasons Over Time"
      description="Shows the distribution of reasons why campaigns were filtered over time. Top to bottom on Y - axis is towards bidding."
      chart={
        <ChartContainer config={chartConfig}>
          <ComposedChart data={data} stackOffset="expand">
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="timestamp"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              ticks={xTicks}
              tickFormatter={dailyAxisFormatter}
            />
            <YAxis tick={false} tickLine={false} axisLine={false} ticks={[]} domain={[0, 1]} />
            <ChartTooltip cursor={false} content={<StackedAreaTooltip />} />
            {reversed.map(reason => {
              const key = reasonKey(reason);
              return (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stackId="a"
                  fill={`var(--color-${key})`}
                  stroke={`var(--color-${key})`}
                  strokeWidth={0}
                  fillOpacity={0.9}
                  isAnimationActive={false}
                />
              );
            })}
          </ComposedChart>
        </ChartContainer>
      }
    />
  );
}

function StackedAreaTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;

  const ts = label as string;
  const row = payload[0]?.payload || {};

  const orderedItems: { key: string; label: string; count: number }[] = [];
  let total = 0;
  for (const reason of REASONS_IN_ORDER) {
    const k = reasonKey(reason);
    const count = typeof row[k] === "number" ? (row[k] as number) : 0;
    if (count <= 0) continue;
    total += count;
    orderedItems.push({ key: k, label: reason, count });
  }

  const MAX = 20;
  let shown = orderedItems;
  let othersTotal = 0;

  if (orderedItems.length > MAX) {
    shown = orderedItems.slice(0, MAX);
    othersTotal = orderedItems.slice(MAX).reduce((s, i) => s + i.count, 0);
  }
  return (
    <div className="min-w-[18rem] max-w-[28rem] rounded-lg border border-border/60 bg-background/95 px-3 py-2 text-xs shadow-xl backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="mb-1 flex items-baseline justify-between gap-4">
        <div className="font-medium">{timeLabelFormatter(ts)}</div>
        <div className="text-muted-foreground">Total: {total.toLocaleString()}</div>
      </div>
      <div className="max-h-72 overflow-auto pr-1">
        <ul className="space-y-1">
          {shown.map(item => {
            return (
              <li key={item.key} className="flex items-center gap-2">
                <span className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: `var(--color-${item.key})` }} />
                <span className="flex-1 leading-tight text-foreground/90 line-clamp-2" title={item.label}>
                  {item.label}
                </span>
                <span className="tabular-nums font-mono text-foreground/80">{item.count.toLocaleString()}</span>
              </li>
            );
          })}
          {othersTotal > 0 && (
            <li className="flex items-center gap-2 text-muted-foreground">
              <span className="h-2 w-2 shrink-0 rounded-sm bg-muted" />
              <span className="flex-1 leading-tight">Other reasons</span>
              <span className="tabular-nums font-mono">{othersTotal.toLocaleString()}</span>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

export default StackedAreaChart;
